import React, { useRef, useCallback } from 'react';
import {
  PRIMARY,
  TEXT_DARK,
  BORDER_LIGHT,
  BG_CARD,
  BG_SURFACE,
} from '../../../components/legacy-ball/table-management/designTokens';

interface ReviewerPanelProps {
  reviewers: string[];
  selectedReviewer: string | 'all';
  onReviewerSelect: (reviewer: string | 'all') => void;
  loading: boolean;
}

const PANEL_WIDTH = 220;

const styles: Record<string, React.CSSProperties> = {
  container: {
    width: `${PANEL_WIDTH}px`,
    minWidth: `${PANEL_WIDTH}px`,
    position: 'sticky',
    top: 0,
    alignSelf: 'flex-start',
    backgroundColor: BG_CARD,
    borderRight: `1px solid ${BORDER_LIGHT}`,
    borderRadius: '8px',
    padding: '8px 0',
    overflowY: 'auto',
    maxHeight: 'calc(100vh - 180px)',
    outline: 'none',
  },
  item: {
    display: 'block',
    width: '100%',
    textAlign: 'left' as const,
    padding: '10px 16px',
    fontSize: '0.875rem',
    fontWeight: 500,
    color: TEXT_DARK,
    backgroundColor: 'transparent',
    border: 'none',
    cursor: 'pointer',
    transition: 'background-color 0.15s, color 0.15s',
    lineHeight: 1.4,
    whiteSpace: 'nowrap' as const,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    outline: 'none',
  },
  itemSelected: {
    display: 'block',
    width: '100%',
    textAlign: 'left' as const,
    padding: '10px 16px',
    fontSize: '0.875rem',
    fontWeight: 600,
    color: '#ffffff',
    backgroundColor: PRIMARY,
    border: 'none',
    cursor: 'pointer',
    transition: 'background-color 0.15s, color 0.15s',
    borderRadius: '4px',
    lineHeight: 1.4,
    whiteSpace: 'nowrap' as const,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    outline: 'none',
  },
  skeletonItem: {
    height: '18px',
    margin: '10px 16px',
    borderRadius: '4px',
    backgroundColor: BG_SURFACE,
  },
};

function SkeletonLoader() {
  return (
    <>
      {[1, 2, 3, 4, 5].map((i) => (
        <div key={i} style={{ ...styles.skeletonItem, width: `${60 + i * 8}%` }} />
      ))}
    </>
  );
}

export function ReviewerPanel({
  reviewers,
  selectedReviewer,
  onReviewerSelect,
  loading,
}: ReviewerPanelProps) {
  const containerRef = useRef<HTMLDivElement>(null);

  // Build the full items list: ['all', ...reviewers]
  const allItems: (string | 'all')[] = ['all', ...reviewers];

  const getCurrentIndex = useCallback(() => {
    if (selectedReviewer === 'all') return 0;
    const idx = reviewers.findIndex(
      (r) => r.toLowerCase() === selectedReviewer.toLowerCase(),
    );
    return idx >= 0 ? idx + 1 : 0;
  }, [selectedReviewer, reviewers]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
      e.preventDefault();

      const currentIdx = getCurrentIndex();
      let nextIdx: number;

      if (e.key === 'ArrowDown') {
        nextIdx = Math.min(currentIdx + 1, allItems.length - 1);
      } else {
        nextIdx = Math.max(currentIdx - 1, 0);
      }

      const nextItem = allItems[nextIdx];
      onReviewerSelect(nextItem);

      // Scroll the selected button into view
      const buttons = containerRef.current?.querySelectorAll('[role="option"]');
      if (buttons && buttons[nextIdx]) {
        (buttons[nextIdx] as HTMLElement).scrollIntoView({ block: 'nearest' });
      }
    },
    [getCurrentIndex, allItems, onReviewerSelect],
  );

  if (loading) {
    return (
      <div style={styles.container} role="listbox" aria-label="Reviewer filter" aria-busy="true">
        <SkeletonLoader />
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      style={styles.container}
      role="listbox"
      aria-label="Reviewer filter"
      tabIndex={0}
      onKeyDown={handleKeyDown}
    >
      <button
        role="option"
        style={selectedReviewer === 'all' ? styles.itemSelected : styles.item}
        onClick={() => onReviewerSelect('all')}
        aria-selected={selectedReviewer === 'all'}
        tabIndex={-1}
      >
        All
      </button>
      {reviewers.map((reviewer) => {
        const isSelected = selectedReviewer.toLowerCase() === reviewer.toLowerCase();
        return (
          <button
            key={reviewer}
            role="option"
            style={isSelected ? styles.itemSelected : styles.item}
            onClick={() => onReviewerSelect(reviewer)}
            aria-selected={isSelected}
            title={reviewer}
            tabIndex={-1}
          >
            {reviewer}
          </button>
        );
      })}
    </div>
  );
}
